import { ShopifyPaymentsSection } from '../components/ShopifyHistory';
import { useMemo, useState } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi, inventoryApi } from '../services/api';
import {
  FileText,
  AlertTriangle,
  Check,
  X,
  Link2,
  DollarSign,
  Plus,
  Eye,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

type InvoiceStatus = 'pending' | 'matched' | 'disputed' | 'approved' | 'paid';
type MatchStatus = 'unmatched' | 'full_match' | 'partial_match' | 'mismatch';
type VarianceType =
  | 'none'
  | 'quantity'
  | 'price'
  | 'both'
  | 'missing_on_invoice'
  | 'extra_on_invoice';

interface Vendor {
  id: string;
  name: string;
}

interface InvoiceListItem {
  id: string;
  invoiceNumber: string;
  invoiceDate?: string;
  dueDate?: string;
  status: InvoiceStatus;
  matchStatus: MatchStatus;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  varianceCents: number;
  vendor: Vendor;
  purchaseOrder?: { id: string; poNumber: string };
}

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit?: string;
  unitPriceCents: number;
  totalCents: number;
  varianceType: VarianceType;
  varianceCents: number;
  quantityOrdered?: number;
  quantityReceived?: number;
  expectedUnitPriceCents?: number;
  inventoryItem?: { name: string };
}

interface InvoiceDetail extends InvoiceListItem {
  lineItems: InvoiceLineItem[];
  purchaseOrder?: { id: string; poNumber: string; items?: any[] };
}

interface MatchResult {
  matchStatus: MatchStatus;
  totalVarianceCents: number;
  lines?: any[];
  summary?: {
    matched: number;
    priceVariances: number;
    quantityVariances: number;
    missingOnInvoice: number;
    extraOnInvoice: number;
  };
}

interface DiscrepancyItem {
  invoiceId: string;
  invoiceNumber: string;
  vendorName: string;
  poNumber?: string;
  status: InvoiceStatus;
  matchStatus: MatchStatus;
  totalCents: number;
  varianceCents: number;
  problemLines: number;
}

interface DiscrepancySummary {
  count: number;
  totalVarianceCents: number;
}

interface PurchaseOrderOption {
  id: string;
  poNumber: string;
  vendor?: { name: string };
}

interface LineItemForm {
  inventoryItemId?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number; // dollars
}

const emptyLineItem: LineItemForm = {
  description: '',
  quantity: 1,
  unit: 'units',
  unitPrice: 0,
};

const defaultFormData = {
  vendorId: '',
  purchaseOrderId: '',
  invoiceNumber: '',
  invoiceDate: '',
  dueDate: '',
  tax: 0, // dollars
  notes: '',
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'matched', label: 'Matched' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
];

const formatCurrency = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;

const formatDate = (date?: string) => {
  if (!date) return '—';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const matchStatusLabel: Record<MatchStatus, string> = {
  unmatched: 'Unmatched',
  full_match: 'Full Match',
  partial_match: 'Partial Match',
  mismatch: 'Mismatch',
};

function MatchBadge({ status }: { status: MatchStatus }) {
  return (
    <span
      className={clsx(
        'badge',
        status === 'full_match' && 'badge-success',
        status === 'partial_match' && 'badge-warning',
        (status === 'mismatch' || status === 'unmatched') && 'badge-danger'
      )}
    >
      {matchStatusLabel[status] || status}
    </span>
  );
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  return (
    <span
      className={clsx(
        'badge capitalize',
        status === 'approved' && 'badge-success',
        status === 'paid' && 'badge-success',
        status === 'matched' && 'badge-info',
        status === 'pending' && 'badge-warning',
        status === 'disputed' && 'badge-danger'
      )}
    >
      {status}
    </span>
  );
}

export default function InvoicesPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [matchInvoice, setMatchInvoice] = useState<InvoiceListItem | null>(null);
  const [matchPoId, setMatchPoId] = useState('');
  const [formData, setFormData] = useState(defaultFormData);
  const [lineItems, setLineItems] = useState<LineItemForm[]>([{ ...emptyLineItem }]);
  const [formError, setFormError] = useState('');
  const [createdMatch, setCreatedMatch] = useState<MatchResult | null>(null);

  // Scan & prefill: photo of the paper invoice, read by a vision model.
  const [scannedImageUrl, setScannedImageUrl] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [scanNote, setScanNote] = useState('');

  const handleScanFile = async (file: File) => {
    setIsParsing(true);
    setScanNote('');
    // Thumbnail straight from the local file — the stored copy lives in a
    // PRIVATE blob store, so its URL can't render in an <img>.
    const localPreview = URL.createObjectURL(file);
    try {
      const res = await invoicesApi.parse(file);
      const { imageUrl, parsed } = res.data?.data || {};
      if (imageUrl) setScannedImageUrl(localPreview);
      if (parsed) {
        setFormData((f) => ({
          ...f,
          invoiceNumber: parsed.invoiceNumber || f.invoiceNumber,
          invoiceDate: parsed.invoiceDate || f.invoiceDate,
          dueDate: parsed.dueDate || f.dueDate,
          tax: parsed.taxCents != null ? parsed.taxCents / 100 : f.tax,
        }));
        if (Array.isArray(parsed.lineItems) && parsed.lineItems.length > 0) {
          setLineItems(
            parsed.lineItems.map((li: any) => ({
              inventoryItemId: '',
              description: li.description || '',
              quantity: li.quantity || 1,
              unit: li.unit || '',
              unitPrice: ((li.unitPriceCents || 0) || 0) / 100,
            }))
          );
        }
        setScanNote('Read from the photo — please check the details before saving.');
      } else {
        setScanNote('Photo attached. It couldn\'t be read automatically — fill in the details below.');
      }
    } catch {
      setScanNote('The photo couldn\'t be uploaded — please try again.');
    } finally {
      setIsParsing(false);
    }
  };

  // One fetch, filtered client-side — the analysis needs the whole book.
  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const response = await invoicesApi.getAll({});
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? (result as InvoiceListItem[]) : [];
    },
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [sortKey, setSortKey] = useState<'invoiceNumber' | 'vendor' | 'invoiceDate' | 'totalCents' | 'status' | 'variance'>('invoiceDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }
    // Numbers/dates default to high→low on first click; text to A→Z.
    else { setSortKey(key); setSortDir(key === 'invoiceDate' || key === 'totalCents' || key === 'variance' ? 'desc' : 'asc'); }
    setVisibleCount(25);
  };
  const [visibleCount, setVisibleCount] = useState(25);

  // Fetch discrepancies summary
  const { data: discrepancyData } = useQuery({
    queryKey: ['invoice-discrepancies'],
    queryFn: async () => {
      const response = await invoicesApi.getDiscrepancies();
      const result = response.data?.data || response.data;
      return (result || { discrepancies: [], summary: { count: 0, totalVarianceCents: 0 } }) as {
        discrepancies: DiscrepancyItem[];
        summary: DiscrepancySummary;
      };
    },
  });

  // Fetch vendors for create modal
  const { data: vendorsData } = useQuery({
    queryKey: ['inventory-vendors'],
    queryFn: async () => {
      const response = await inventoryApi.getVendors();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? (result as Vendor[]) : [];
    },
  });

  // Fetch invoice detail for the view modal
  const { data: invoiceDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['invoice', viewInvoiceId],
    enabled: !!viewInvoiceId,
    queryFn: async () => {
      const response = await invoicesApi.getById(viewInvoiceId as string);
      const result = response.data?.data || response.data;
      return (result || null) as InvoiceDetail | null;
    },
  });

  // POs for the create modal (filtered by selected vendor)
  const { data: createPos } = useQuery({
    queryKey: ['purchase-orders', formData.vendorId],
    enabled: showCreateModal && !!formData.vendorId,
    queryFn: async () => {
      const response = await inventoryApi.getPurchaseOrders({ vendorId: formData.vendorId });
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? (result as PurchaseOrderOption[]) : [];
    },
  });

  // POs for the match modal (filtered by the invoice's vendor)
  const { data: matchPos } = useQuery({
    queryKey: ['purchase-orders', matchInvoice?.vendor.id],
    enabled: !!matchInvoice && !matchInvoice.purchaseOrder,
    queryFn: async () => {
      const response = await inventoryApi.getPurchaseOrders({
        vendorId: matchInvoice?.vendor.id as string,
      });
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? (result as PurchaseOrderOption[]) : [];
    },
  });

  const allInvoices = Array.isArray(invoicesData) ? invoicesData : [];

  // ---- Spend analysis, straight from the book
  const analysis = useMemo(() => {
    const dated = allInvoices.filter((i) => i.invoiceDate);
    const now = Date.now();
    const d30 = now - 30 * 86400000; const d60 = now - 60 * 86400000; const d90 = now - 90 * 86400000;
    let last30 = 0, prior30 = 0, last30Count = 0;
    const vendor90 = new Map<string, number>();
    const byMonth = new Map<string, number>();
    for (const inv of dated) {
      const t = new Date(inv.invoiceDate as string).getTime();
      if (Number.isNaN(t)) continue;
      const cents = inv.totalCents || 0;
      if (t >= d30) { last30 += cents; last30Count += 1; }
      else if (t >= d60) prior30 += cents;
      if (t >= d90) {
        const vn = inv.vendor?.name || 'Vendor';
        vendor90.set(vn, (vendor90.get(vn) || 0) + cents);
      }
      const mk = (inv.invoiceDate as string).slice(0, 7);
      byMonth.set(mk, (byMonth.get(mk) || 0) + cents);
    }
    const vendors90 = [...vendor90.entries()].sort((a, b) => b[1] - a[1]);
    const vendor90Total = vendors90.reduce((sm, [, c]) => sm + c, 0);
    const months = [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-12);
    const maxMonth = Math.max(1, ...months.map(([, c]) => c));
    const trendPct = prior30 > 0 ? Math.round(((last30 - prior30) / prior30) * 100) : null;
    return { last30, last30Count, trendPct, vendors90: vendors90.slice(0, 6), vendor90Total, months, maxMonth };
  }, [allInvoices]);

  const vendorNames = useMemo(
    () => [...new Set(allInvoices.map((i) => i.vendor?.name).filter(Boolean))].sort() as string[],
    [allInvoices]
  );

  const invoices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: InvoiceListItem, b: InvoiceListItem): number => {
      switch (sortKey) {
        case 'invoiceNumber': return dir * String(a.invoiceNumber || '').localeCompare(String(b.invoiceNumber || ''));
        case 'vendor': return dir * String(a.vendor?.name || '').localeCompare(String(b.vendor?.name || ''));
        case 'invoiceDate': return dir * String(a.invoiceDate || '').localeCompare(String(b.invoiceDate || ''));
        case 'totalCents': return dir * ((a.totalCents || 0) - (b.totalCents || 0));
        case 'status': return dir * String(a.status || '').localeCompare(String(b.status || ''));
        case 'variance': return dir * ((a.varianceCents || 0) - (b.varianceCents || 0));
        default: return 0;
      }
    };
    return allInvoices
      .filter((i) => statusFilter === 'all' || i.status === statusFilter)
      .filter((i) => vendorFilter === 'all' || i.vendor?.name === vendorFilter)
      .filter((i) => !q || (i.invoiceNumber || '').toLowerCase().includes(q) || (i.vendor?.name || '').toLowerCase().includes(q))
      .sort(cmp);
  }, [allInvoices, statusFilter, vendorFilter, searchTerm, sortKey, sortDir]);
  const visible = invoices.slice(0, visibleCount);
  const vendors = Array.isArray(vendorsData) ? vendorsData : [];
  const discrepancies = discrepancyData?.discrepancies || [];

  // Clickable, toggle-able column header — click to sort, click again to flip.
  const SortTh = ({ k, label }: { k: typeof sortKey; label: string }) => (
    <th
      className="table-header cursor-pointer select-none hover:text-slate-900"
      onClick={() => toggleSort(k)}
      title={`Sort by ${label}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k
          ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
          : <ChevronsUpDown className="w-3.5 h-3.5 opacity-30" />}
      </span>
    </th>
  );
  const discrepancySummary = discrepancyData?.summary || { count: 0, totalVarianceCents: 0 };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    queryClient.invalidateQueries({ queryKey: ['invoice-discrepancies'] });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await invoicesApi.create({
        vendorId: formData.vendorId,
        purchaseOrderId: formData.purchaseOrderId || undefined,
        invoiceNumber: formData.invoiceNumber,
        invoiceDate: formData.invoiceDate || undefined,
        dueDate: formData.dueDate || undefined,
        taxCents: formData.tax ? Math.round(formData.tax * 100) : undefined,
        notes: formData.notes || undefined,
        imageUrl: scannedImageUrl || undefined,
        lineItems: lineItems
          .filter((li) => li.description.trim())
          .map((li) => ({
            inventoryItemId: li.inventoryItemId || undefined,
            description: li.description,
            quantity: li.quantity,
            unit: li.unit || undefined,
            unitPriceCents: Math.round(li.unitPrice * 100),
          })),
      });
      return response;
    },
    onSuccess: (response) => {
      const result = response.data?.data || response.data;
      setScannedImageUrl(null);
      setScanNote('');
      invalidateAll();
      setShowCreateModal(false);
      setFormData(defaultFormData);
      setLineItems([{ ...emptyLineItem }]);
      setFormError('');
      const matchResult: MatchResult | undefined = result?.matchResult;
      if (
        matchResult &&
        (matchResult.totalVarianceCents !== 0 || matchResult.matchStatus !== 'full_match')
      ) {
        setCreatedMatch(matchResult);
      }
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create invoice');
    },
  });

  // Match mutation
  const matchMutation = useMutation({
    mutationFn: ({ id, poId }: { id: string; poId?: string }) =>
      invoicesApi.match(id, poId),
    onSuccess: (response) => {
      const result = (response.data?.data || response.data) as MatchResult;
      invalidateAll();
      if (viewInvoiceId) {
        queryClient.invalidateQueries({ queryKey: ['invoice', viewInvoiceId] });
      }
      setMatchInvoice(null);
      setMatchPoId('');
      if (result) setCreatedMatch(result);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to match invoice');
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) => invoicesApi.approve(id),
    onSuccess: (_data, id) => {
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });

  // Dispute mutation
  const disputeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoicesApi.dispute(id, reason),
    onSuccess: (_data, vars) => {
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ['invoice', vars.id] });
    },
  });

  const handleMatch = (invoice: InvoiceListItem) => {
    if (invoice.purchaseOrder) {
      matchMutation.mutate({ id: invoice.id });
    } else {
      setMatchInvoice(invoice);
      setMatchPoId('');
    }
  };

  const handleDispute = (id: string) => {
    const reason = window.prompt('Reason for disputing this invoice?') || undefined;
    if (reason === undefined) return; // cancelled
    disputeMutation.mutate({ id, reason });
  };

  const updateLineItem = (index: number, patch: Partial<LineItemForm>) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...patch } : li)));
  };

  const addLineItem = () => setLineItems((prev) => [...prev, { ...emptyLineItem }]);

  const removeLineItem = (index: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== index));

  const lineItemTotal = (li: LineItemForm) => li.quantity * li.unitPrice;
  const lineItemsSubtotal = lineItems.reduce((sum, li) => sum + lineItemTotal(li), 0);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Invoices & Reconciliation</h1>
            <p className="text-sm md:text-base text-slate-500">
              Match invoices against purchase orders and resolve discrepancies
            </p>
          </div>
          <HelpButton featureId="invoices" />
        </div>
        <button
          onClick={() => {
            setFormData(defaultFormData);
            setLineItems([{ ...emptyLineItem }]);
            setFormError('');
            setShowCreateModal(true);
          }}
          className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto py-3 sm:py-2"
        >
          <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
          New Invoice
        </button>
      </div>

      <ShopifyPaymentsSection />
      {/* Discrepancies Banner */}
      {discrepancySummary.count > 0 && (
        <div className="card p-4 bg-warning-50 border-warning-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-warning-800">
                {discrepancySummary.count} invoice{discrepancySummary.count === 1 ? '' : 's'} with
                discrepancies — {formatCurrency(discrepancySummary.totalVarianceCents)} total variance
              </p>
              {discrepancies.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {discrepancies.slice(0, 6).map((d) => (
                    <button
                      key={d.invoiceId}
                      onClick={() => setViewInvoiceId(d.invoiceId)}
                      className="px-2 py-1 bg-white rounded text-xs text-warning-700 border border-warning-200 hover:bg-warning-100 transition-colors"
                    >
                      {d.invoiceNumber} · {d.vendorName} · {formatCurrency(d.varianceCents)} (
                      {d.problemLines} line{d.problemLines === 1 ? '' : 's'})
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => { setStatusFilter(f.value); setVisibleCount(25); }}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap',
              statusFilter === f.value
                ? 'bg-slate-800 text-white'
                : 'bg-white text-slate-600 active:bg-slate-100'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Spend analysis — the book, read for you */}
      {allInvoices.length > 3 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400">Spend · last 30 days</p>
              <p className="text-3xl font-semibold text-slate-900 tabular-nums mt-1">
                ${Math.round(analysis.last30 / 100).toLocaleString('en-US')}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {analysis.last30Count} invoices
                {analysis.trendPct !== null && (
                  <span className={clsx('ml-1.5 rounded px-1.5 py-0.5 text-xs font-medium', analysis.trendPct > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700')}>
                    {analysis.trendPct > 0 ? '+' : ''}{analysis.trendPct}% vs prior 30
                  </span>
                )}
              </p>
            </div>
            <div className="card p-5 lg:col-span-2">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Where it goes · last 90 days</p>
              <div className="space-y-2">
                {analysis.vendors90.map(([vn, cents]) => (
                  <div key={vn}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-slate-700 truncate">{vn}</span>
                      <span className="tabular-nums text-slate-900 font-medium whitespace-nowrap">
                        ${Math.round(cents / 100).toLocaleString('en-US')}
                        <span className="text-slate-400 font-normal ml-1.5">{analysis.vendor90Total ? Math.round((cents / analysis.vendor90Total) * 100) : 0}%</span>
                      </span>
                    </div>
                    <div className="h-1 rounded-full bg-slate-100 mt-1 overflow-hidden">
                      <div className="h-1 rounded-full bg-primary-500" style={{ width: `${analysis.vendor90Total ? Math.max(3, Math.round((cents / analysis.vendor90Total) * 100)) : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {analysis.months.length > 2 && (
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Monthly spend</p>
              <div className="flex items-end gap-1.5 h-24">
                {analysis.months.map(([mk, cents]) => (
                  <div key={mk} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-primary-500/80 rounded-sm hover:bg-primary-600 transition-colors"
                      title={`${mk}: $${Math.round(cents / 100).toLocaleString('en-US')}`}
                      style={{ height: `${Math.max(4, Math.round((cents / analysis.maxMonth) * 88))}px` }} />
                    <span className="text-[10px] text-slate-400">{mk.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Search + vendor filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(25); }}
          placeholder="Search invoice # or vendor…"
          className="input flex-1 max-w-sm"
        />
        <select value={vendorFilter} onChange={(e) => { setVendorFilter(e.target.value); setVisibleCount(25); }} className="input w-full sm:w-64">
          <option value="all">All vendors</option>
          {vendorNames.map((vn) => <option key={vn} value={vn}>{vn}</option>)}
        </select>
      </div>

      {/* Invoice list */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No invoices found</p>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {visible.map((inv) => (
              <div key={inv.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-semibold text-slate-900 truncate">{inv.invoiceNumber}</p>
                      <StatusBadge status={inv.status} />
                      <MatchBadge status={inv.matchStatus} />
                    </div>
                    <p className="text-sm text-slate-500">{inv.vendor?.name}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                      <div>
                        <span className="text-slate-400">PO:</span>{' '}
                        <span className="font-medium">{inv.purchaseOrder?.poNumber || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Date:</span>{' '}
                        <span className="font-medium">{formatDate(inv.invoiceDate)}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Total:</span>{' '}
                        <span className="font-medium">{formatCurrency(inv.totalCents)}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Variance:</span>{' '}
                        <span
                          className={clsx(
                            'font-medium',
                            inv.varianceCents !== 0 ? 'text-danger-600' : 'text-slate-700'
                          )}
                        >
                          {formatCurrency(inv.varianceCents)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => setViewInvoiceId(inv.id)}
                    className="btn btn-sm btn-secondary flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => handleMatch(inv)}
                    className="btn btn-sm btn-secondary flex items-center gap-1"
                    disabled={matchMutation.isPending}
                  >
                    <Link2 className="w-4 h-4" /> Match
                  </button>
                  <button
                    onClick={() => approveMutation.mutate(inv.id)}
                    className="btn btn-sm btn-secondary flex items-center gap-1"
                    disabled={approveMutation.isPending || inv.status === 'approved' || inv.status === 'paid'}
                  >
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleDispute(inv.id)}
                    className="btn btn-sm btn-ghost flex items-center gap-1"
                    disabled={disputeMutation.isPending}
                  >
                    <X className="w-4 h-4" /> Dispute
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <SortTh k="invoiceNumber" label="Invoice #" />
                  <SortTh k="vendor" label="Vendor" />
                  <th className="table-header">PO</th>
                  <SortTh k="invoiceDate" label="Date" />
                  <SortTh k="totalCents" label="Total" />
                  <SortTh k="status" label="Status" />
                  <th className="table-header">Match</th>
                  <SortTh k="variance" label="Variance" />
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{inv.invoiceNumber}</td>
                    <td className="table-cell">{inv.vendor?.name}</td>
                    <td className="table-cell">{inv.purchaseOrder?.poNumber || '—'}</td>
                    <td className="table-cell">{formatDate(inv.invoiceDate)}</td>
                    <td className="table-cell">{formatCurrency(inv.totalCents)}</td>
                    <td className="table-cell">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="table-cell">
                      <MatchBadge status={inv.matchStatus} />
                    </td>
                    <td className="table-cell">
                      <span
                        className={clsx(
                          'font-medium',
                          inv.varianceCents !== 0 ? 'text-danger-600' : 'text-slate-700'
                        )}
                      >
                        {formatCurrency(inv.varianceCents)}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button
                          onClick={() => setViewInvoiceId(inv.id)}
                          className="btn btn-sm btn-secondary"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMatch(inv)}
                          className="btn btn-sm btn-secondary"
                          title="Run 3-way match"
                          disabled={matchMutation.isPending}
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => approveMutation.mutate(inv.id)}
                          className="btn btn-sm btn-secondary"
                          title="Approve"
                          disabled={
                            approveMutation.isPending ||
                            inv.status === 'approved' ||
                            inv.status === 'paid'
                          }
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDispute(inv.id)}
                          className="btn btn-sm btn-ghost"
                          title="Dispute"
                          disabled={disputeMutation.isPending}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">Showing {visible.length} of {invoices.length.toLocaleString('en-US')} invoices</p>
            {invoices.length > visibleCount && (
              <button onClick={() => setVisibleCount((c) => c + 100)} className="btn btn-sm btn-secondary">Show more</button>
            )}
          </div>
        </>
      )}

      {/* Match result toast / panel */}
      {createdMatch && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-md p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary-600" /> Match Result
              </h2>
              <button
                onClick={() => setCreatedMatch(null)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Status:</span>
                <MatchBadge status={createdMatch.matchStatus} />
              </div>
              <p className="text-sm">
                <span className="text-slate-500">Total variance:</span>{' '}
                <span
                  className={clsx(
                    'font-semibold',
                    createdMatch.totalVarianceCents !== 0 ? 'text-danger-600' : 'text-slate-800'
                  )}
                >
                  {formatCurrency(createdMatch.totalVarianceCents)}
                </span>
              </p>
              {createdMatch.summary && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-emerald-700 font-medium">{createdMatch.summary.matched}</p>
                    <p className="text-xs text-emerald-600">Matched</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-amber-700 font-medium">{createdMatch.summary.priceVariances}</p>
                    <p className="text-xs text-amber-600">Price variances</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-amber-700 font-medium">
                      {createdMatch.summary.quantityVariances}
                    </p>
                    <p className="text-xs text-amber-600">Qty variances</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2">
                    <p className="text-red-700 font-medium">
                      {createdMatch.summary.missingOnInvoice + createdMatch.summary.extraOnInvoice}
                    </p>
                    <p className="text-xs text-red-600">Missing / extra</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setCreatedMatch(null)} className="btn btn-primary">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View detail modal */}
      {viewInvoiceId && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                {invoiceDetail ? invoiceDetail.invoiceNumber : 'Invoice'}
              </h2>
              <button
                onClick={() => setViewInvoiceId(null)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isLoadingDetail ? (
              <div className="text-center py-12">Loading...</div>
            ) : !invoiceDetail ? (
              <div className="text-center py-12 text-slate-500">Invoice not found</div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-slate-400">Vendor</p>
                    <p className="font-medium">{invoiceDetail.vendor?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Linked PO</p>
                    <p className="font-medium">{invoiceDetail.purchaseOrder?.poNumber || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Status</p>
                    <StatusBadge status={invoiceDetail.status} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Match</p>
                    <MatchBadge status={invoiceDetail.matchStatus} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Subtotal</p>
                    <p className="font-medium">{formatCurrency(invoiceDetail.subtotalCents)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Tax</p>
                    <p className="font-medium">{formatCurrency(invoiceDetail.taxCents)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Total</p>
                    <p className="font-medium">{formatCurrency(invoiceDetail.totalCents)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Variance</p>
                    <p
                      className={clsx(
                        'font-medium',
                        invoiceDetail.varianceCents !== 0 ? 'text-danger-600' : 'text-slate-700'
                      )}
                    >
                      {formatCurrency(invoiceDetail.varianceCents)}
                    </p>
                  </div>
                </div>

                {/* 3-way line items */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="table-header">Item</th>
                        <th className="table-header">Ordered</th>
                        <th className="table-header">Received</th>
                        <th className="table-header">Invoiced</th>
                        <th className="table-header">Exp. Price</th>
                        <th className="table-header">Inv. Price</th>
                        <th className="table-header">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(invoiceDetail.lineItems || []).map((li) => (
                        <tr
                          key={li.id}
                          className={clsx(
                            li.varianceType === 'none' && 'bg-emerald-50/40',
                            (li.varianceType === 'price' ||
                              li.varianceType === 'quantity' ||
                              li.varianceType === 'both') &&
                              'bg-amber-50/60',
                            (li.varianceType === 'missing_on_invoice' ||
                              li.varianceType === 'extra_on_invoice') &&
                              'bg-red-50/60'
                          )}
                        >
                          <td className="table-cell">
                            <p className="font-medium">
                              {li.inventoryItem?.name || li.description}
                            </p>
                            {li.varianceType !== 'none' && (
                              <p className="text-xs text-slate-500 capitalize">
                                {li.varianceType.replace(/_/g, ' ')}
                              </p>
                            )}
                          </td>
                          <td className="table-cell">{li.quantityOrdered ?? '—'}</td>
                          <td className="table-cell">{li.quantityReceived ?? '—'}</td>
                          <td className="table-cell">
                            {li.quantity} {li.unit || ''}
                          </td>
                          <td className="table-cell">
                            {li.expectedUnitPriceCents != null
                              ? formatCurrency(li.expectedUnitPriceCents)
                              : '—'}
                          </td>
                          <td className="table-cell">{formatCurrency((li.unitPriceCents || 0))}</td>
                          <td className="table-cell">
                            <span
                              className={clsx(
                                'font-medium',
                                li.varianceCents !== 0 ? 'text-danger-600' : 'text-slate-600'
                              )}
                            >
                              {formatCurrency(li.varianceCents)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(!invoiceDetail.lineItems || invoiceDetail.lineItems.length === 0) && (
                        <tr>
                          <td colSpan={7} className="table-cell text-center text-slate-500 py-6">
                            No line items
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Detail actions */}
                <div className="flex flex-wrap justify-end gap-2 mt-6">
                  <button
                    onClick={() => handleMatch(invoiceDetail)}
                    className="btn btn-secondary flex items-center gap-1"
                    disabled={matchMutation.isPending}
                  >
                    <Link2 className="w-4 h-4" /> Match
                  </button>
                  <button
                    onClick={() => approveMutation.mutate(invoiceDetail.id)}
                    className="btn btn-secondary flex items-center gap-1"
                    disabled={
                      approveMutation.isPending ||
                      invoiceDetail.status === 'approved' ||
                      invoiceDetail.status === 'paid'
                    }
                  >
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleDispute(invoiceDetail.id)}
                    className="btn btn-ghost flex items-center gap-1"
                    disabled={disputeMutation.isPending}
                  >
                    <X className="w-4 h-4" /> Dispute
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Match PO picker modal */}
      {matchInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-md p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary-600" /> Match to PO
              </h2>
              <button
                onClick={() => {
                  setMatchInvoice(null);
                  setMatchPoId('');
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-600 mb-4 text-sm">
              No PO is linked to {matchInvoice.invoiceNumber}. Select a purchase order from{' '}
              {matchInvoice.vendor?.name} to run the 3-way match.
            </p>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Purchase Order
                </label>
                <select
                  className="input w-full"
                  value={matchPoId}
                  onChange={(e) => setMatchPoId(e.target.value)}
                >
                  <option value="">Select a PO...</option>
                  {(matchPos || []).map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.poNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setMatchInvoice(null);
                  setMatchPoId('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  matchMutation.mutate({ id: matchInvoice.id, poId: matchPoId || undefined })
                }
                className="btn btn-primary"
                disabled={!matchPoId || matchMutation.isPending}
              >
                {matchMutation.isPending ? 'Matching...' : 'Run Match'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create invoice modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-2xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Invoice</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}

            {/* Scan & prefill — photograph the paper invoice; a vision model
                fills the form, the human just verifies. */}
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">Scan the paper invoice</p>
                  <p className="text-xs text-slate-500">
                    {isParsing ? 'Reading the photo…' : scanNote || 'Take a photo and the details fill themselves in.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {scannedImageUrl && !isParsing && (
                    <a href={scannedImageUrl} target="_blank" rel="noopener noreferrer">
                      <img src={scannedImageUrl} alt="Scanned invoice" className="w-10 h-10 rounded-lg object-cover border border-slate-200" />
                    </a>
                  )}
                  <label className={`text-sm px-3 py-2 rounded-lg font-medium cursor-pointer ${isParsing ? 'bg-slate-200 text-slate-400' : 'bg-primary-600 text-white'}`}>
                    {isParsing ? 'Reading…' : scannedImageUrl ? 'Rescan' : 'Scan'}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      className="hidden"
                      disabled={isParsing}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleScanFile(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate();
              }}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Vendor *</label>
                    <select
                      className="input w-full"
                      value={formData.vendorId}
                      onChange={(e) =>
                        setFormData({ ...formData, vendorId: e.target.value, purchaseOrderId: '' })
                      }
                      required
                    >
                      <option value="">Select vendor...</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Invoice Number *
                    </label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Link Purchase Order (optional)
                  </label>
                  <select
                    className="input w-full"
                    value={formData.purchaseOrderId}
                    onChange={(e) =>
                      setFormData({ ...formData, purchaseOrderId: e.target.value })
                    }
                    disabled={!formData.vendorId}
                  >
                    <option value="">No PO linked</option>
                    {(createPos || []).map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.poNumber}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Invoice Date
                    </label>
                    <input
                      type="date"
                      className="input w-full"
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      className="input w-full"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    />
                  </div>
                </div>

                {/* Line items */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-700">Line Items</p>
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="btn btn-sm btn-secondary flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add Line
                    </button>
                  </div>
                  <div className="space-y-3">
                    {lineItems.map((li, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 md:col-span-4">
                          <label className="block text-xs text-slate-500 mb-1">Description</label>
                          <input
                            type="text"
                            className="input w-full"
                            value={li.description}
                            onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                          />
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs text-slate-500 mb-1">Qty</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input w-full"
                            value={li.quantity}
                            onChange={(e) =>
                              updateLineItem(idx, { quantity: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block text-xs text-slate-500 mb-1">Unit</label>
                          <input
                            type="text"
                            className="input w-full"
                            value={li.unit}
                            onChange={(e) => updateLineItem(idx, { unit: e.target.value })}
                          />
                        </div>
                        <div className="col-span-4 md:col-span-3">
                          <label className="block text-xs text-slate-500 mb-1">Unit Price ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input w-full"
                            value={li.unitPrice}
                            onChange={(e) =>
                              updateLineItem(idx, { unitPrice: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div className="col-span-12 md:col-span-1 flex md:justify-center">
                          <button
                            type="button"
                            onClick={() => removeLineItem(idx)}
                            className="p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg"
                            title="Remove line"
                            disabled={lineItems.length === 1}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tax + totals */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tax ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input w-full"
                      value={formData.tax}
                      onChange={(e) =>
                        setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="flex flex-col justify-end text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="font-medium">
                        {formatCurrency(Math.round(lineItemsSubtotal * 100))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" /> Total
                      </span>
                      <span className="font-semibold text-slate-900">
                        {formatCurrency(
                          Math.round((lineItemsSubtotal + (formData.tax || 0)) * 100)
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    className="input w-full"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
